import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  ChevronDownIcon,
  ChevronUpIcon,
  Controller,
  RawButton,
  Text,
  useFormContext,
} from '@common-fe/common-fe';
import _ from 'lodash';

import { IFieldLevelAccessProperties, IFieldLevelAccessSetup } from '../role.types';

import FieldLevelAccessGroupItem from './FieldLevelAccessGroupItem';

const PER_STACK = 10;

const dividerTrigger = (index: number, length: number, perStack: number): boolean => {
  return (index + 1) % perStack === 0 && index !== length - 1;
};

interface Props {
  name: string;
  testId?: string;
  defaultExpanded?: boolean;
  items?: Partial<IFieldLevelAccessProperties>;
  isOverridableProperties?: boolean;
  isExternalExpandButton?: boolean;
  isAllRendered?: boolean;
  isEditMode?: boolean;

  withDivider?: boolean;
  withTopBorder?: boolean;
}

const MAX_RENDERED_ITEMS = 3;
const FieldLevelAccessGroup: React.FC<Props> = ({
  testId,
  isOverridableProperties,
  defaultExpanded,
  name,
  items = {},
  isExternalExpandButton,
  isAllRendered = false,
  isEditMode,
  withDivider,
  withTopBorder = true,
}) => {
  const { control } = useFormContext<IFieldLevelAccessSetup>();
  const [expanded, setExpanded] = useState(true);
  const [isShowMode, setIsShowMore] = useState(isAllRendered);
  useEffect(() => {
    if (defaultExpanded !== undefined) {
      setExpanded(defaultExpanded);
    }
  }, [defaultExpanded]);
  const handleToggle = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);
  const formattedItems = useMemo(() => {
    const keys = Object.keys(items).map((key) => {
      return {
        key,
        name: _.startCase(_.camelCase(key)),
        value: items[key],
      };
    });
    return keys;
  }, [items]);
  const sortedItems = useMemo(() => {
    return _.sortBy(formattedItems, (o) => o.key);
  }, [formattedItems]);

  const currentList = useMemo(() => {
    if (isShowMode) {
      return sortedItems;
    }
    return sortedItems.slice(0, MAX_RENDERED_ITEMS);
  }, [sortedItems, isShowMode]);
  const handleToggleExpand = useCallback(() => {
    setIsShowMore(!isShowMode);
  }, [isShowMode]);
  return (
    <Box
      direction="column"
      border={withTopBorder && { side: 'top', size: 'small', color: 'border1' }}
      data-testid={testId}
    >
      {!isExternalExpandButton && (
        <RawButton data-testid={`${testId}_chevron`} onClick={handleToggle}>
          <Box align="center" direction="row" pad={{ vertical: 'xxs' }} gap="xxxs">
            {expanded ? (
              <ChevronUpIcon color="iconPrimary"></ChevronUpIcon>
            ) : (
              <ChevronDownIcon color="iconPrimary"></ChevronDownIcon>
            )}

            <Text data-testid={`${testId}_title`} size="xxlarge" weight={400}>
              {name}
            </Text>
          </Box>
        </RawButton>
      )}
      {expanded && (
        <Box>
          <Box direction="row" margin={{ bottom: 'l' }}>
            <Box width="280px" />
            <Box direction="row" width="145px" justify="between">
              <Text size="medium" weight={400}>
                View
              </Text>
              <Text size="medium" weight={400}>
                Edit
              </Text>
              <Text size="medium" weight={400}>
                Hide
              </Text>
            </Box>
          </Box>

          {currentList.map((item, index) => (
            <Fragment key={item.key}>
              <Controller
                name={
                  isOverridableProperties ? `overridableProperties.${item.key}` : `properties.${item.key}`
                }
                control={control}
                render={({ field }) => {
                  return (
                    <FieldLevelAccessGroupItem
                      testId={`${testId}_radio_${item.key}`}
                      index={index + 1}
                      name={item.name}
                      value={field.value || items[item.key]}
                      onSetValue={field.onChange}
                      isEditMode={isEditMode}
                    />
                  );
                }}
              />

              {dividerTrigger(index, currentList.length, PER_STACK) && withDivider && (
                <Box border={{ side: 'bottom', size: 'small', color: 'border1' }} margin={{ bottom: 'm' }} />
              )}
            </Fragment>
          ))}

          <RawButton
            data-testid={`${testId}_${isShowMode ? 'collapse' : 'show_all'}_button`}
            onClick={handleToggleExpand}
          >
            <Text color="textAccent" weight={400}>
              {isShowMode ? 'Collapse' : 'Show All'}
            </Text>
          </RawButton>
        </Box>
      )}
    </Box>
  );
};

export default FieldLevelAccessGroup;
